import {filter, Subscription} from 'rxjs';
import {useEffect, useState} from 'react';

import type {
  Conversation,
  ConversationParticipant,
} from '@stripe-internal/conversations-client';

import {TypingRowDotCom} from './Chat/TypingRowDotCom';

type Props = {
  conversation: Conversation;
};

function useConversationTypers(conversation: Conversation) {
  const [typers, setTypers] = useState<ConversationParticipant[]>([]);

  useEffect(() => {
    const sub = conversation.typingObservable().subscribe(setTypers);
    return () => sub.unsubscribe();
  }, [conversation]);

  return typers;
}

export function DotComLoadingIndicator({conversation}: Props) {
  const [botTyping, setBotTyping] = useState<boolean>(
    !!conversation.context.placeholderConversation,
  );
  const [agentTyping, setAgentTyping] = useState(false);
  const typers = useConversationTypers(conversation);

  const botTyper = typers.some(
    (typer) => typer.id !== conversation.user.id && typer.role === 'bot',
  );
  const agentTyper = typers.some(
    (typer) => typer.id !== conversation.user.id && typer.role === 'agent',
  );

  useEffect(() => {
    if (botTyper) {
      setBotTyping(true);
    }

    if (agentTyper) {
      setAgentTyping(true);
    }
  }, [botTyper, agentTyper]);

  useEffect(() => {
    const sub = new Subscription();
    sub.add(
      conversation
        .eventsObservable()
        .pipe(filter((event) => event.name === 'conversation-message-added'))
        .subscribe(() => {
          setBotTyping(false);
          setAgentTyping(false);
        }),
    );
    return () => sub.unsubscribe();
  }, [conversation]);

  useEffect(() => {
    const noMessageLoadedDelay = 500;
    const timeoutId = setTimeout(() => {
      if (agentTyping && !agentTyper) {
        setBotTyping(false);
        setAgentTyping(false);
      }
    }, noMessageLoadedDelay);

    return () => clearTimeout(timeoutId);
  }, [agentTyping, agentTyper]);

  return botTyping || agentTyping ? <TypingRowDotCom /> : null;
}
